import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography } from "@material-tailwind/react";
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CalendarIcon,
  CheckIcon,
  CubeIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { WrenchScrewdriverIcon as WrenchSolid } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { getCompletedOrderDetails, getCompletedOrderProducts } from "@/store/slices/newDeliverySlice";
import ShowLoader from "@/components/Loader";
import moment from "moment";
import { useTranslation } from "react-i18next";

function fmtMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function localized(value, lang = "en") {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return value;
  if (Array.isArray(value))
    return value.map((v) => localized(v, lang)).filter(Boolean).join(", ");
  if (typeof value === "object") return value[lang] ?? value.en ?? value.ar ?? "";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY - hh:mm A") : "—";
}

function deductionReasonText(reason) {
  if (reason == null || reason === "") return null;
  if (Array.isArray(reason)) {
    const parts = reason.filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof reason === "string") return reason;
  return String(reason);
}

export default function CompletedOrderSummary() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const { t } = useTranslation("delivery");

  const { user } = useSelector((state) => state.user);

  const { completed_order_details, loading } = useSelector(
    (state) => state.delivery
  );

  const { completed_order_products } = useSelector((state) => state.delivery);

  const products = Array.isArray(completed_order_products)
    ? completed_order_products
    : completed_order_products?.data ?? [];

  const [fetchSettled, setFetchSettled] = useState(false);

  useEffect(() => {
    if (!id) {
      setFetchSettled(false);
      return;
    }

    setFetchSettled(false);
    let cancelled = false;

    Promise.all([
      dispatch(getCompletedOrderDetails({ id })),
      dispatch(getCompletedOrderProducts({ id })),
    ]).finally(() => {
      if (!cancelled) setFetchSettled(true);
    });

    return () => {
      cancelled = true;
    };
  }, [id, dispatch]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/delivery-partner/home?tab=completed", { replace: true });
    }
  };

  const details = completed_order_details || {};

  const taskType = details?.type ?? "—";

  const orderTitle =
    details?.order_name ?? details?.order_id ?? id ?? "—";

  const deductionLabel = deductionReasonText(
    details?.amount_deducted_reason
  );

  const [showDeductionDetails, setShowDeductionDetails] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      {loading && <ShowLoader />}

      <div className="bg-[#065C36] text-white pt-4 pb-10 sm:pb-12 px-4 sm:px-6 shrink-0">
        <div className="max-w-7xl mx-auto w-full">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 text-xs sm:text-sm hover:opacity-90 transition text-left"
          >
            <ArrowLeftIcon className="h-4 w-4 shrink-0" />

            <span className="hidden sm:inline">
              {t("back_to_completed_order_listing")}
            </span>

            <span className="sm:hidden">
              {t("back")}
            </span>
          </button>

          <Typography
            variant="h3"
            className="text-center font-bold tracking-tight mt-5 sm:mt-6 text-xl sm:text-3xl break-words"
          >
            #{orderTitle}
          </Typography>
        </div>
      </div>

      <div className="max-w-7xl w-full mx-auto mt-6 sm:mt-10 px-4 sm:px-6 pb-20 flex-1">
        {fetchSettled &&
          !loading &&
          id &&
          Object.keys(details).length === 0 && (
            <div className="text-center py-12 text-gray-500 rounded-xl border border-dashed border-gray-200 bg-white">
              {t("no_summary_data_available")}
            </div>
          )}

        {(loading || !fetchSettled || Object.keys(details).length > 0) && (
          <>
            <div className="space-y-4 mb-8">
              <div className="bg-[#F3F4F6] p-4 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-gray-200 shadow-sm">
                <div className="bg-[#065C36] w-9 h-9 rounded-full flex items-center justify-center shrink-0">
                  <CalendarIcon className="h-5 w-5 text-white" />
                </div>

                <Typography className="text-sm font-medium text-gray-500">
                  {t("created_on")}{" "}
                  <span className="text-gray-900 font-bold pl-3">
                    {formatDateTime(details?.created_on)}
                  </span>
                </Typography>
              </div>

              <div className="bg-gradient-to-r from-[#ECFDF5] to-white p-4 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-green-100">
                <div className="bg-[#F4B11F] w-9 h-9 rounded-full flex items-center justify-center shrink-0">
                  <CheckIcon className="h-4 w-4 text-white" />
                </div>

                <Typography className="text-sm font-medium text-gray-500">
                  {t("completed_on")}{" "}
                  <span className="text-gray-900 font-bold pl-3">
                    {formatDateTime(details?.delivered_on)}
                  </span>
                </Typography>
              </div>

              <div className="bg-[#F3F4F6] p-4 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border border-gray-200 shadow-sm">
                <div className="bg-[#065C36] w-9 h-9 rounded-full flex items-center justify-center shrink-0">
                  <WrenchSolid className="h-4 w-4 text-white" />
                </div>

                <Typography className="text-sm font-medium text-gray-500">
                  {t("task_type")}:{" "}
                  <span className="text-gray-900 font-bold capitalize pl-3">
                    {taskType}
                  </span>
                </Typography>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Total Amount Card */}
              <div
                className="rounded-xl px-6 py-6 border border-[#FEECC5] shadow-sm"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF9EB 0%, #FEECC5 100%)",
                }}
              >
                <Typography className="text-[13px] font-bold text-[#3B82F6] mb-1 tracking-wide">
                  {t("total_amount")}{" "}
                  <span className="lowercase font-normal text-[10px] opacity-80">
                    ({t("final_payable")}):
                  </span>
                </Typography>

                <Typography
                  variant="h3"
                  className="text-[#3B82F6] font-black text-2xl sm:text-3xl"
                >
                  {fmtMoney(details?.total_amount)}
                </Typography>
              </div>

              {/* Total Paid Card */}
              <div
                className="rounded-xl px-6 py-6 border-2 border-[#B2D1C1] shadow-sm"
                style={{
                  background:
                    "linear-gradient(180deg, #E8F3ED 0%, #B2D1C1 100%)",
                }}
              >
                <Typography className="text-[13px] font-bold text-[#065C36] mb-1 tracking-wide">
                  {t("total_sar_paid")}:
                </Typography>

                <Typography
                  variant="h3"
                  className="text-[#065C36] font-black text-2xl sm:text-3xl"
                >
                  {fmtMoney(details?.total_amount_paid || 0)}
                </Typography>
              </div>

              {/* Total Pending Card */}
              <div
                className="rounded-xl px-6 py-6 border border-[#FEECC5] shadow-sm"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF9EB 0%, #FEECC5 100%)",
                }}
              >
                <Typography className="text-[13px] font-bold text-[#F4B11F] mb-1 tracking-wide">
                  {t("total_sar_pending")}:
                </Typography>

                <Typography
                  variant="h3"
                  className="text-[#F4B11F] font-black text-2xl sm:text-3xl"
                >
                  {fmtMoney(details?.total_amount_pending || 0)}
                </Typography>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Typography
                  variant="h4"
                  color="blue-gray"
                  className="mb-6 font-bold"
                >
                  {t("order_summary")}
                </Typography>

                <div className="space-y-4">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col sm:flex-row items-stretch">
                    {/* <div className={`w-1.5 shrink-0 ${
                      details?.payment_status === "paid" ? "bg-green-600" : 
                      details?.payment_status === "partial_payment" ? "bg-amber-500" : "bg-red-500"
                    }`} /> */}

                    <div className="flex-1 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-4 min-w-0">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                            details?.payment_status === "paid"
                              ? "bg-green-50"
                              : details?.payment_status === "partial_payment"
                              ? "bg-amber-50"
                              : "bg-red-50"
                          }`}
                        >
                          <BanknotesIcon
                            className={`h-7 w-7 ${
                              details?.payment_status === "paid"
                                ? "text-green-900"
                                : details?.payment_status ===
                                  "partial_payment"
                                ? "text-amber-700"
                                : "text-red-900"
                            }`}
                          />
                        </div>

                        <div>
                          <Typography className="font-bold text-gray-800">
                            {t("payment_status")}
                          </Typography>

                          {details?.payment_status === "partial_payment" && (
                            <Typography className="text-[10px] font-bold text-amber-600 uppercase">
                              {t("partial_settlement_received")}
                            </Typography>
                          )}
                        </div>
                      </div>

                      {/* Status Text Value */}
                      <Typography
                        className={`font-black text-lg sm:text-xl uppercase shrink-0 ${
                          details?.payment_status === "paid"
                            ? "text-green-600"
                            : details?.payment_status === "partial_payment"
                            ? "text-amber-600"
                            : "text-red-500"
                        }`}
                      >
                        {details?.payment_status === "paid"
                          ? t("payment_status_paid")
                          : details?.payment_status === "partial_payment"
                          ? t("payment_status_partial")
                          : details?.payment_status
                          ? details.payment_status.replace("_", " ")
                          : t("payment_status_pending")}
                      </Typography>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-green-50 w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                        <TruckIcon className="h-7 w-7 text-green-900" />
                      </div>

                      <Typography className="font-bold text-gray-800">
                        {t("delivery_charges")}
                      </Typography>
                    </div>

                    <Typography className="font-black text-gray-900 shrink-0">
                       {user?.currency_code ?? ''}  {fmtMoney(details?.delivery_charge)}
                    </Typography>
                  </div>

                  {/* Installation Charges */}
                  <div className="bg-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                        <WrenchSolid className="h-5 w-5 text-blue-900" />
                      </div>

                      <Typography className="font-bold text-gray-800">
                        {t("installation_charges")}
                      </Typography>
                    </div>

                    <Typography className="font-black text-gray-900 shrink-0">
                        {user?.currency_code ?? ''} {fmtMoney(details?.installation_charge)}
                    </Typography>
                  </div>

                  {/* <div className="bg-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-sm border border-red-100">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-red-50 w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                        <div className="h-1 w-4 bg-red-400 rounded" />
                      </div>
                      <div className="min-w-0">
                        <Typography className="font-bold text-gray-800">
                          {t("amount_deducted")}
                        </Typography>
                        {deductionLabel && (
                          <Typography className="text-[10px] font-bold text-red-500 uppercase mt-0.5 break-words">
                            {deductionLabel}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <Typography className="font-black text-red-500 shrink-0">
                      - {details?.rfq_currency_code} {fmtMoney(details?.amount_deducted)}
                    </Typography>
                  </div> */}

                  <div className="bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden">
                    {/* Header */}
                    <div
                      className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer"
                      onClick={() =>
                        setShowDeductionDetails(!showDeductionDetails)
                      }
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="bg-red-50 w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                          <div className="h-1 w-4 bg-red-400 rounded" />
                        </div>

                        <div className="min-w-0">
                          <Typography className="font-bold text-gray-800">
                            {t("amount_deducted")}
                          </Typography>

                          {/* {deductionLabel && (
                            <Typography className="text-[10px] font-bold text-red-500 uppercase mt-0.5 break-words">
                              {deductionLabel}
                            </Typography>
                          )} */}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Typography className="font-black text-red-500 shrink-0">
                          - {details?.rfq_currency_code} {fmtMoney(details?.amount_deducted)}
                        </Typography>

                        {/* Dropdown Arrow */}
                        {details?.deduction_details?.length > 0 && (
                          <span className="text-gray-400 text-sm">
                            {showDeductionDetails ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Dropdown Content */}
                    {showDeductionDetails &&
                      details?.deduction_details?.length > 0 && (
                        <div className="border-t border-red-100 bg-red-50/30 px-4 sm:px-5 py-4 space-y-3">
                          {details.deduction_details.map((item, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-xl p-4 border border-red-100 shadow-sm"
                            >
                              <div className="flex justify-between items-start gap-3">
                                <div>
                                  <Typography className="font-bold text-gray-800 text-sm">
                                    {item.product_name || "—"}
                                  </Typography>

                                  <Typography className="text-xs text-gray-500 mt-1">
                                    {t("qty")}: {item.quantity}
                                  </Typography>

                                  <Typography className="text-xs text-gray-500">
                                    {t("delivery_charge")}:  {user?.currency_code ?? ''} {" "}
                                    {fmtMoney(item.delivery_charge)}
                                  </Typography>

                                  {item.amount_deducted_reason && (
                                    <Typography className="text-[10px] font-bold text-red-500 uppercase mt-1">
                                      {item.amount_deducted_reason}
                                    </Typography>
                                  )}
                                </div>

                                <div className="text-right">
                                  <Typography className="text-red-500 font-bold text-sm">
                                    -  {details?.rfq_currency_code} {fmtMoney(item.amount_deducted)}
                                  </Typography>

                                  <Typography className="text-[11px] text-gray-500 mt-1">
                                    {item.deduction_percentage}%{" "}
                                    {t("deduction")}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>

                  <div className="bg-[#EBF3FF] rounded-[24px] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-[#D1E4FF] mt-8 sm:mt-10">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-[#D1E4FF] w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                        <BanknotesIcon className="h-7 w-7 text-[#2563EB]" />
                      </div>

                      <Typography className="text-base font-bold text-[#4B5563]">
                        {t("total_amount")}
                      </Typography>
                    </div>

                    <Typography
                      className={`text-xl font-bold shrink-0 ${
                        Number(details?.total_amount) < 0
                          ? "text-red-500"
                          : "text-[#2563EB]"
                      }`}
                    >
                      {details?.rfq_currency_code} {fmtMoney(details?.total_amount)}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="mt-12">
              <Typography variant="h4" color="blue-gray" className="mb-6 font-bold">
                {t("products", "Products")}
                {products.length > 0 && (
                  <span className="ml-2 text-sm font-medium text-gray-400">
                    ({products.length})
                  </span>
                )}
              </Typography>

              {products.length === 0 ? (
                <div className="text-center py-10 text-gray-500 rounded-xl border border-dashed border-gray-200 bg-white">
                  {t("no_products_available", "No products available")}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {products.map((product, index) => {
                    const delivered = product?.delivery_status === "delivered";

                    return (
                      <div
                        key={`${product?.delivery_assigned_order_id ?? "p"}-${product?.sku_number ?? index}-${index}`}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col"
                      >
                        {/* Image */}
                        <div className="relative bg-gray-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
                          {product?.product_image ? (
                            <img
                              src={product.product_image}
                              alt={product?.product_name || "product"}
                              loading="lazy"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <CubeIcon className="h-12 w-12 text-gray-300" />
                          )}

                          {/* Delivery status badge */}
                          {product?.delivery_status && (
                            <span
                              className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${
                                delivered
                                  ? "bg-green-50 text-green-700 border border-green-100"
                                  : "bg-amber-50 text-amber-700 border border-amber-100"
                              }`}
                            >
                              {delivered
                                ? t("delivered", "Delivered")
                                : String(product.delivery_status).replace(/_/g, " ")}
                            </span>
                          )}
                        </div>

                        {/* Body */}
                        <div className="p-4 sm:p-5 flex flex-col gap-3 flex-1">
                          <div className="min-w-0">
                            {product?.category_name && (
                              <Typography className="text-[10px] font-bold text-[#065C36] uppercase tracking-wide mb-1">
                                {product.category_name}
                              </Typography>
                            )}

                            <Typography className="font-bold text-gray-800 break-words">
                              {product?.product_name || "—"}
                            </Typography>
                          </div>

                          <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                            <span>
                              {t("sku", "SKU")}:{" "}
                              <span className="font-bold text-gray-800">
                                {product?.sku_number || "—"}
                              </span>
                            </span>

                            <span>
                              {t("qty")}:{" "}
                              <span className="font-black text-gray-900">
                                {product?.quantity_assigned ?? 0}
                              </span>
                            </span>
                          </div>

                          {/* {product?.is_installation && (
                            <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-2">
                              <div className="bg-blue-50 w-7 h-7 rounded-full flex items-center justify-center shrink-0">
                                <WrenchSolid className="h-3.5 w-3.5 text-blue-900" />
                              </div>
                              <Typography className="text-xs font-bold text-blue-900">
                                {t("installation_required", "Installation included")}
                              </Typography>
                            </div>
                          )} */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}